import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTranslation } from 'react-i18next'
import { 
  Wallet, 
  CreditCard, 
  Banknote, 
  PiggyBank,
  Building,
  Info,
  CheckCircle,
  ArrowRight,
  Plus,
  TrendingUp,
  DollarSign
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useAccountsStore } from '@/store/accountsStore'
import { toast } from 'sonner'

interface AccountSetupStepProps {
  onNext: () => void
  onBack: () => void
}

const accountTypes = [
  {
    id: 'checking',
    name: 'Checking Account',
    icon: <Wallet className="h-7 w-7 text-white" />,
    description: 'For daily transactions and bill payments',
    color: 'bg-gradient-to-br from-blue-500 to-blue-600',
    hoverColor: 'hover:from-blue-600 hover:to-blue-700',
    ringColor: 'ring-blue-500/30'
  },
  {
    id: 'savings',
    name: 'Savings Account',
    icon: <PiggyBank className="h-7 w-7 text-white" />,
    description: 'For building emergency funds and goals',
    color: 'bg-gradient-to-br from-emerald-500 to-emerald-600',
    hoverColor: 'hover:from-emerald-600 hover:to-emerald-700',
    ringColor: 'ring-emerald-500/30'
  },
  {
    id: 'credit',
    name: 'Credit Card',
    icon: <CreditCard className="h-7 w-7 text-white" />,
    description: 'Track spending and build credit history',
    color: 'bg-gradient-to-br from-purple-500 to-purple-600',
    hoverColor: 'hover:from-purple-600 hover:to-purple-700',
    ringColor: 'ring-purple-500/30'
  },
  {
    id: 'cash',
    name: 'Cash',
    icon: <Banknote className="h-7 w-7 text-white" />,
    description: 'Physical cash and wallet money',
    color: 'bg-gradient-to-br from-amber-500 to-orange-500',
    hoverColor: 'hover:from-amber-600 hover:to-orange-600',
    ringColor: 'ring-amber-500/30'
  },
  {
    id: 'investment',
    name: 'Investment',
    icon: <TrendingUp className="h-7 w-7 text-white" />,
    description: 'Stocks, bonds, and other investments',
    color: 'bg-gradient-to-br from-indigo-500 to-indigo-600',
    hoverColor: 'hover:from-indigo-600 hover:to-indigo-700',
    ringColor: 'ring-indigo-500/30'
  },
  {
    id: 'business',
    name: 'Business Account',
    icon: <Building className="h-7 w-7 text-white" />,
    description: 'For business and professional use',
    color: 'bg-gradient-to-br from-slate-500 to-slate-600',
    hoverColor: 'hover:from-slate-600 hover:to-slate-700',
    ringColor: 'ring-slate-500/30'
  }
]

const formSchema = z.object({
  name: z.string().min(1, 'Account name is required'),
  type: z.string().min(1, 'Please select an account type'),
  currency: z.string().min(1, 'Please select a currency'),
  initialBalance: z.string().optional(),
})

export function AccountSetupStep({ onNext }: AccountSetupStepProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { addAccount } = useAccountsStore()
  const [selectedType, setSelectedType] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      type: '',
      currency: 'USD',
      initialBalance: '0',
    },
  })

  const currencies = [
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  ]

  const handleTypeSelect = (type: string) => {
    setSelectedType(type)
    form.setValue('type', type)
  }

  async function onSubmit(values: z.infer<typeof formSchema>) {
    if (!user?.id) {
      toast.error('You must be logged in to continue')
      return
    }

    setIsSubmitting(true)
    try {
      const selectedAccountType = accountTypes.find(t => t.id === values.type)
      const accountData = {
        name: values.name,
        type: values.type,
        currency: values.currency,
        balance: parseFloat(values.initialBalance || '0'),
        color: selectedAccountType?.color.includes('blue') ? '#3B82F6' : 
               selectedAccountType?.color.includes('emerald') ? '#10B981' :
               selectedAccountType?.color.includes('purple') ? '#8B5CF6' :
               selectedAccountType?.color.includes('amber') ? '#F59E0B' :
               selectedAccountType?.color.includes('indigo') ? '#6366F1' : 
               selectedAccountType?.color.includes('slate') ? '#64748B' : '#439476',
        icon: values.type === 'checking' ? 'Wallet' :
              values.type === 'savings' ? 'PiggyBank' :
              values.type === 'credit' ? 'CreditCard' :
              values.type === 'cash' ? 'Banknote' :
              values.type === 'investment' ? 'TrendingUp' : 
              values.type === 'business' ? 'Building' : 'Wallet'
      }

      await addAccount(accountData)
      toast.success(t('onboarding.accountSetup.success', 'Account created successfully!'))
      onNext()
    } catch (error) {
      console.error('Error creating account:', error)
      toast.error(t('onboarding.accountSetup.error', 'Failed to create account. Please try again.'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const selectedAccountType = accountTypes.find(type => type.id === selectedType)

  return (
    <div className="space-y-10">
      {/* Hero Section */}
      <div className="text-center space-y-8">
        <div className="relative mx-auto w-20 h-20 mb-6">
          <div className="absolute inset-0 bg-gradient-to-r from-secondary to-primary dark:from-primary-dark dark:to-secondary-dark rounded-3xl opacity-20 animate-pulse"></div>
          <div className="relative bg-gradient-to-r from-secondary to-primary dark:from-primary-dark dark:to-secondary-dark rounded-3xl p-4 shadow-xl">
            <Wallet className="h-12 w-12 text-white" />
          </div>
        </div>
        
        <div className="space-y-4 max-w-2xl mx-auto">
          <h3 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary dark:from-primary-dark dark:to-secondary-dark bg-clip-text text-transparent">
            {t('onboarding.accountSetup.title', 'Add Your First Account')}
          </h3>
          <p className="text-lg text-text-secondary dark:text-text-dark-secondary leading-relaxed">
            {t('onboarding.accountSetup.description', 'Start by adding your primary account to begin tracking your finances.')}
          </p>
        </div>
      </div>

      {/* Account Type Selection */}
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <h4 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">
            {t('onboarding.accountSetup.selectType', 'Choose Your Account Type')}
          </h4>
          <p className="text-text-secondary dark:text-text-dark-secondary">
            Select the type that best describes your account
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {accountTypes.map((type) => (
            <Card
              key={type.id}
              className={`group p-6 cursor-pointer transition-all duration-300 border-2 ${
                selectedType === type.id
                  ? `ring-4 ${type.ringColor} border-transparent shadow-xl scale-[1.02]`
                  : 'border-border dark:border-border-dark hover:border-secondary/30 dark:hover:border-primary-dark/30 hover:shadow-lg hover:scale-[1.01]'
              } bg-background-secondary/50 dark:bg-background-dark-secondary/50 backdrop-blur-xl backdrop-saturate-150 rounded-2xl`}
              onClick={() => handleTypeSelect(type.id)}
            >
              <div className="flex flex-col items-center space-y-4 text-center">
                <div className={`p-3 rounded-2xl ${type.color} ${type.hoverColor} shadow-lg group-hover:shadow-xl transition-all duration-300 ${
                  selectedType === type.id ? 'scale-110' : 'group-hover:scale-105'
                }`}>
                  {type.icon}
                </div>
                
                <div className="space-y-2">
                  <h5 className="font-semibold text-text-primary dark:text-text-dark-primary text-base">
                    {t(`onboarding.accountSetup.types.${type.id}`, type.name)}
                  </h5>
                  <p className="text-sm text-text-secondary dark:text-text-dark-secondary leading-relaxed">
                    {t(`onboarding.accountSetup.descriptions.${type.id}`, type.description)}
                  </p>
                </div>

                {selectedType === type.id && (
                  <div className="absolute top-3 right-3">
                    <div className="bg-secondary dark:bg-primary-dark rounded-full p-1 shadow-lg">
                      <CheckCircle className="h-4 w-4 text-white" />
                    </div>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Account Details Form */}
      {selectedType && (
        <Card className="p-8 bg-background-secondary/50 dark:bg-background-dark-secondary/50 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-2xl shadow-sm">
          <div className="space-y-8">
            <div className="flex items-center gap-4 pb-4 border-b border-border dark:border-border-dark">
              <div className={`p-3 rounded-2xl ${selectedAccountType?.color} shadow-lg`}>
                {selectedAccountType?.icon}
              </div>
              <div className="space-y-1">
                <h4 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">
                  {t('onboarding.accountSetup.accountDetails', 'Account Details')}
                </h4>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                  Provide some basic information about your {selectedAccountType?.name.toLowerCase()}
                </p>
              </div>
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <FormLabel className="text-text-primary dark:text-text-dark-primary font-medium text-base">
                          {t('onboarding.accountSetup.accountName', 'Account Name')}
                        </FormLabel>
                        <FormControl>
                          <Input 
                            placeholder={t('onboarding.accountSetup.accountNamePlaceholder', 'e.g., Main Checking')}
                            {...field}
                            className="h-12 bg-background-input dark:bg-background-dark-input border border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark focus:ring-2 focus:ring-secondary/20 dark:focus:ring-primary-dark/20 rounded-xl transition-all duration-200 text-base"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="currency"
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <FormLabel className="text-text-primary dark:text-text-dark-primary font-medium text-base">
                          {t('onboarding.accountSetup.currency', 'Currency')}
                        </FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className="h-12 bg-background-input dark:bg-background-dark-input border border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark rounded-xl text-base">
                              <SelectValue placeholder={t('onboarding.accountSetup.selectCurrency', 'Select currency')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="rounded-xl border border-border dark:border-border-dark bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150">
                            {currencies.map((currency) => (
                              <SelectItem key={currency.code} value={currency.code} className="rounded-lg">
                                <div className="flex items-center gap-3">
                                  <span className="font-medium text-lg">{currency.symbol}</span>
                                  <div className="flex flex-col">
                                    <span className="font-medium">{currency.code}</span>
                                    <span className="text-sm text-text-secondary dark:text-text-dark-secondary">{currency.name}</span>
                                  </div>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="initialBalance"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel className="text-text-primary dark:text-text-dark-primary font-medium text-base">
                        {t('onboarding.accountSetup.initialBalance', 'Current Balance')}
                        <span className="text-text-secondary dark:text-text-dark-secondary text-sm font-normal ml-2">
                          (Optional)
                        </span>
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <div className="absolute left-4 top-1/2 -translate-y-1/2">
                            <DollarSign className="h-5 w-5 text-text-secondary dark:text-text-dark-secondary" />
                          </div>
                          <Input 
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            {...field}
                            className="h-12 pl-12 bg-background-input dark:bg-background-dark-input border border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark focus:ring-2 focus:ring-secondary/20 dark:focus:ring-primary-dark/20 rounded-xl transition-all duration-200 text-base"
                          />
                        </div>
                      </FormControl>
                      <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                        Enter the current balance of this account
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-center pt-6">
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="bg-primary hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 text-white px-10 py-4 text-lg font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-3 h-14 min-w-[200px] active:scale-[0.98]"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {t('common.creating', 'Creating...')}
                      </>
                    ) : (
                      <>
                        <Plus className="w-5 h-5" />
                        {t('onboarding.accountSetup.createAccount', 'Create Account')}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </Card>
      )}

      {/* Info Card */}
      <Card className="p-6 bg-gradient-to-br from-secondary/5 to-primary/5 dark:from-primary-dark/5 dark:to-secondary-dark/5 border border-secondary/20 dark:border-primary-dark/20 backdrop-blur-xl backdrop-saturate-150 rounded-2xl shadow-sm">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-2xl bg-secondary/10 dark:bg-primary-dark/10 border border-secondary/20 dark:border-primary-dark/20 flex-shrink-0">
            <Info className="h-5 w-5 text-secondary dark:text-primary-dark" />
          </div>
          <div className="space-y-2">
            <h5 className="font-semibold text-text-primary dark:text-text-dark-primary">
              {t('onboarding.accountSetup.tipTitle', 'Pro Tip')}
            </h5>
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary leading-relaxed">
              {t('onboarding.accountSetup.tipDescription', 'You can add more accounts later from the accounts page. Start with your primary account to get the most accurate financial overview.')}
            </p>
          </div>
        </div>
      </Card>
    </div>
  )
} 